import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { MIC_ACCESS_ERROR } from '@/app/definitions'
import { useCampaignFiles } from '@/app/module/campaigns/component/campaign-files-provider'
import { useItemContext } from '@/app/module/campaigns/component/item-provider'
import { useGetCampaignFile } from '@/app/module/campaigns/hooks/use-get-campaign-file'
import { usePersonalizationList } from '@/app/module/campaigns/hooks/use-personalization-list'
import { useSaveCampaignFile } from '@/app/module/campaigns/hooks/use-save-campaign-file'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'
import { FileUploadType } from '@/app/types'

const emptyArr: unknown[] = []
const emptyObject: Record<string, unknown> = {}

export const useCampaignContentProps = (msgId: string) => {
  const dispatch = useAppDispatch()
  const { item } = useItemContext()
  const [getCampaignFile] = useGetCampaignFile()
  const [saveCampaignFile] = useSaveCampaignFile()
  const orgId = useSelector(selectOrgId)
  const { files: filesState } = useCampaignFiles()
  const campaignId = item && 'id' in item ? item.id : undefined
  const { isFetching: loading } = esApi.endpoints.getCampaign.useQueryState(
    { orgId: orgId.toString(), campaignId: String(campaignId) },
    { skip: !campaignId },
  )

  const defaultLanguage = item?.uiStore?.defaultLanguage || ''
  const files = useMemo(() => filesState?.data || emptyObject, [filesState])
  const languages = useMemo(() => item?.uiStore?.languages || emptyArr, [item])
  const personalizationList = usePersonalizationList()

  const getFile = useCallback((file: string) => getCampaignFile({ msgId, file }), [getCampaignFile, msgId])
  const onMicAccessError = useCallback(() => {
    dispatch(
      createNotification({
        'mic-access': {
          type: 'error',
          message: MIC_ACCESS_ERROR,
        },
      }),
    )
  }, [dispatch])
  const saveFileHandler = useCallback(
    (file: FileUploadType) =>
      saveCampaignFile({
        msgId,
        item: file,
      }),
    [saveCampaignFile, msgId],
  )

  return {
    defaultLanguage,
    files,
    languages,
    loading,
    personalizationList,
    getFile,
    onMicAccessError,
    saveFileHandler,
  }
}

export default useCampaignContentProps
